import React from 'react';
import { AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { StandardAppLayout } from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import Loader from 'src/shared/components/loader/Loader';
import { PrimaryButton, SecondaryButton } from 'src/shared/components/button/Button';
import { useListStacCollectionsQuery, type StacCollection } from 'src/shared/state/api-slices/stacCatalogueEngineSlice';
import CollectionListTable from './components/collection-list/CollectionListTable';

/** Every dataset the platform holds, in the engine's versioned catalogue (ADR-016). */
const StacCatalogueList = () => {
    const navigate = useNavigate();
    const { data, isLoading, error, refetch } = useListStacCollectionsQuery();

    const openCollection = (collection: StacCollection) => navigate(`/admin/catalogue/${collection.id}`);

    const mainContent = (() => {
        if (isLoading) {
            return (
                <div className='flex justify-center py-24' data-testid='stac-catalogue-loading'>
                    <Loader />
                </div>
            );
        }

        // An unreadable catalogue shows nothing rather than a list missing the rows that
        // failed: a super user cannot tell a short list from a complete one.
        if (error) {
            return (
                <div className='flex flex-col items-center justify-center py-24 gap-4 text-center' data-testid='stac-catalogue-error'>
                    <div className='flex items-center justify-center w-16 h-16 rounded-full bg-red-0'>
                        <AlertCircle className='w-8 h-8 text-red-100' />
                    </div>
                    <div className='flex flex-col items-center gap-1 max-w-sm'>
                        <h3 className='text-lg font-semibold text-primary'>Unable to load the catalogue</h3>
                        <p className='text-sm text-grey-70'>The engine returned an error. Try again, or check your connection.</p>
                    </div>
                    <SecondaryButton onClick={() => refetch()} data-testid='stac-catalogue-retry'>
                        Retry
                    </SecondaryButton>
                </div>
            );
        }

        return <CollectionListTable collections={data?.collections ?? []} onRowClick={openCollection} />;
    })();

    return (
        <StandardAppLayout
            title='Geospatial Catalogue'
            otherActions={
                <PrimaryButton onClick={() => navigate('/admin/catalogue/new')} data-testid='stac-catalogue-new'>
                    New dataset
                </PrimaryButton>
            }
            mainContent={mainContent}
        />
    );
};

export default StacCatalogueList;
